import React from 'react';
import { formatDate, DATE, type DateInput } from '@koob/dates';
import { ServiceApplicationDateKindEnum } from '@koob/enums';
import {
  getExtraOccurrencePax,
  getServiceApplicationDate,
  sortExtraOccurrences,
} from '@koob/utils';
import AdditionalServiceChip from '../AdditionalServiceChip';
import AdditionalServicesBand, {
  AdditionalServicesBandScopeEnum,
  AdditionalServicesBandStateEnum,
} from '../AdditionalServicesBand';
import type { Translation } from './types';

/**
 * KD-3581 §3 — a read-only services band: the day's, and the global one that
 * closes each frame of the version being compared (AC03).
 *
 * An old version records what was selected, never what was on offer at the
 * time, so the band only knows two states: `selected`, or the dashed empty
 * one. Nothing here is actionable: no `onAction` leaves the band inert, no
 * `onRemove` leaves the chips without a cross.
 */
export default function TripReadOnlyServicesBand({
  scope,
  extras,
  departureDate,
  frame,
  entries,
  t,
}: {
  scope: AdditionalServicesBandScopeEnum;
  extras?: any[];
  departureDate: DateInput;
  frame: any;
  // KD-3805: the experience's catalogue, to tell a global transfer apart (see
  // getServiceApplicationDate); absent, `extra.transfer` decides.
  entries?: any[];
  t: Translation;
}) {
  // KD-3650 — an old version records its occurrences in API order; the chips
  // are numbered by `rank`, exactly as the live composer's band is.
  const selected = sortExtraOccurrences(extras ?? []);
  const isDay = scope === AdditionalServicesBandScopeEnum.DAY;

  if (selected.length === 0) {
    return (
      <AdditionalServicesBand
        scope={scope}
        state={AdditionalServicesBandStateEnum.UNAVAILABLE}
        emptyLabel={t(
          isDay ? 'compose.services.dayEmpty' : 'compose.services.globalEmpty',
        )}
      />
    );
  }

  return (
    <AdditionalServicesBand
      scope={scope}
      state={AdditionalServicesBandStateEnum.SELECTED}
      title={t(
        isDay ? 'compose.services.dayTitle' : 'compose.services.globalTitle',
      )}
      subtitle={t(
        isDay
          ? 'compose.services.daySelected'
          : 'compose.services.globalSelected',
        { count: selected.length },
      )}
    >
      {selected.map((extra: any) => {
        // The services of a version are read off the days themselves, so they
        // already carry the array index `getServiceApplicationDate` reasons
        // in, like everywhere else in the composer.
        const application = getServiceApplicationDate({
          departureDate,
          extra,
          frame,
          entries,
        });

        // KD-3650 — the party is read as a whole, like the modal and the
        // amount on the chip do: an occurrence carries its pax count on
        // `adults` or `adultCount` depending on which pass last wrote it.
        const { adults, childrenBirthdates } = getExtraOccurrencePax(extra);
        const travellers = [
          adults ? t('compose.services.adults', { count: adults }) : null,
          childrenBirthdates.length
            ? t('compose.services.children', {
                count: childrenBirthdates.length,
              })
            : null,
        ].filter(Boolean);

        return (
          <AdditionalServiceChip
            key={`${extra.id}-${extra.dayIndex}-${
              extra.parentExtraId ?? 'root'
            }-${extra.occurrenceKey ?? 'single'}`}
            scope={scope}
            name={extra?.name ?? ''}
            dateLabel={
              application
                ? application.kind === ServiceApplicationDateKindEnum.RANGE
                  ? t('compose.services.dateRange', {
                      from: formatDate(application.from, DATE),
                      to: formatDate(application.to, DATE),
                    })
                  : formatDate(application.from, DATE)
                : null
            }
            travellersLabel={travellers.length ? travellers.join(' · ') : null}
          />
        );
      })}
    </AdditionalServicesBand>
  );
}
